/**
 * "Point at a field" — Fluent v9 rendition of references/ui-spec.md §4.
 *
 * The page stays visible and inert: hovering outlines the documented control under the cursor,
 * clicking pins its entry. Every event of a press sequence is swallowed in the capture phase —
 * asking what a control does must never be the same as pressing it (HelpInspect.test.tsx).
 * No scrim: a scrim hides the form being read.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@fluentui/react-components';
import { Dismiss16Regular, Map20Regular, TapSingle20Regular } from '@fluentui/react-icons';
import { anchorIndex, HELP_PANEL_ATTR, stepAtElement } from './dom';
import { cardPosition, rectOf, type Rect } from './position';
import { fmt, HELP_STRINGS } from './strings';
import { HELP_TABSTER_ATTRS } from './tabster';
import { localizedContext, localizedSection, localizedStep, type HelpLanguage, type HelpStep, type PageHelp } from './types';

const CARD_WIDTH = 330;
const CARD_HEIGHT = 190;
const GROW = 3;
const MISS_MS = 2200;

/** Not just `click`: a select opens on mousedown and a menu item commits on mouseup. */
export const SWALLOWED_EVENTS = ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'dblclick', 'contextmenu'] as const;

interface HelpInspectProps {
  help: PageHelp;
  /** Already role-filtered. */
  steps: HelpStep[];
  language: HelpLanguage;
  onTourFrom: (step: HelpStep) => void;
  onExit: () => void;
}

const inGuide = (target: EventTarget | null) => target instanceof Element && target.closest(`[${HELP_PANEL_ATTR}]`) !== null;

export const HelpInspect = ({ help, steps, language, onTourFrom, onExit }: HelpInspectProps) => {
  const s = HELP_STRINGS[language];
  const index = useMemo(() => anchorIndex(steps), [steps]);
  const [hover, setHover] = useState<{ step: HelpStep; rect: Rect } | null>(null);
  const [pinned, setPinned] = useState<{ step: HelpStep; element: HTMLElement; rect: Rect } | null>(null);
  const [miss, setMiss] = useState<{ x: number; y: number } | null>(null);
  const lastTarget = useRef<EventTarget | null>(null);
  const missTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    document.body.classList.add('pct-help-inspecting');
    return () => document.body.classList.remove('pct-help-inspecting');
  }, []);

  useEffect(() => {
    const swallow = (e: Event) => {
      if (inGuide(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
    };
    const onMove = (e: PointerEvent) => {
      if (inGuide(e.target) || e.target === lastTarget.current) return; // cached per hovered element
      lastTarget.current = e.target;
      const hit = stepAtElement(e.target, index);
      setHover(hit ? { step: hit.step, rect: rectOf(hit.element) } : null);
    };
    const onClick = (e: MouseEvent) => {
      if (inGuide(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      const hit = stepAtElement(e.target, index);
      if (hit) {
        setMiss(null);
        setPinned({ ...hit, rect: rectOf(hit.element) });
      } else {
        setPinned(null);
        setMiss({ x: e.clientX, y: e.clientY });
        clearTimeout(missTimer.current);
        missTimer.current = setTimeout(() => setMiss(null), MISS_MS);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onExit();
    };
    const onViewport = () => {
      setPinned((p) => (p && p.element.isConnected ? { ...p, rect: rectOf(p.element) } : null));
      setHover(null);
      lastTarget.current = null;
    };
    SWALLOWED_EVENTS.forEach((type) => window.addEventListener(type, swallow, true));
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('click', onClick, true);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onViewport);
    window.addEventListener('scroll', onViewport, true);
    return () => {
      SWALLOWED_EVENTS.forEach((type) => window.removeEventListener(type, swallow, true));
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('click', onClick, true);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onViewport);
      window.removeEventListener('scroll', onViewport, true);
      clearTimeout(missTimer.current);
    };
  }, [index, onExit]);

  const pinnedShown = pinned ? localizedStep(pinned.step, language) : null;
  const pinnedContext = pinned?.step.context ? localizedContext(help, pinned.step.context, language) : undefined;
  const cardPos = pinned ? cardPosition(pinned.rect, { width: CARD_WIDTH, height: CARD_HEIGHT }) : null;
  const bannerLabel = hover ? localizedStep(hover.step, language).title : s.inspectBanner;
  const box = (r: Rect) => ({ top: r.top - GROW, left: r.left - GROW, width: r.width + GROW * 2, height: r.height + GROW * 2 });

  return (
    <>
      <div className="pct-help-banner" {...{ [HELP_PANEL_ATTR]: 'inspect' }} {...HELP_TABSTER_ATTRS} role="status">
        <TapSingle20Regular style={{ flex: 'none' }} />
        <span className="pct-help-banner-label">{bannerLabel}</span>
        <span className="pct-help-banner-esc">{s.inspectEsc}</span>
        <button type="button" className="pct-help-icon-button" aria-label={s.inspectLeave} title={s.inspectLeave} onClick={onExit}>
          <Dismiss16Regular />
        </button>
      </div>

      {hover && (!pinned || hover.step !== pinned.step) && <div className="pct-help-highlight" style={box(hover.rect)} aria-hidden="true" />}
      {pinned && <div className="pct-help-highlight pct-help-highlight--pinned" style={box(pinned.rect)} aria-hidden="true" />}

      {pinned && pinnedShown && cardPos && (
        <div
          className="pct-help-float"
          role="dialog"
          aria-label={fmt(s.inspectCardAria, { title: pinnedShown.title })}
          {...{ [HELP_PANEL_ATTR]: 'inspect' }}
          {...HELP_TABSTER_ATTRS}
          style={{ top: cardPos.top, left: cardPos.left, width: CARD_WIDTH }}
        >
          {pinned.step.section && (
            <div className="pct-help-float-section" style={{ marginTop: 0 }}>
              {localizedSection(help, pinned.step.section, language)}
            </div>
          )}
          <div className="pct-help-float-title">
            {pinnedShown.title}
            {!pinnedShown.translated && (
              <span className="pct-help-pill" title={s.untranslated}>
                EN
              </span>
            )}
          </div>
          <div className="pct-help-float-body">{pinnedShown.body}</div>
          {pinnedContext && <div className="pct-help-card-context">{pinnedContext}</div>}
          <div className="pct-help-float-actions">
            <Button appearance="subtle" size="small" icon={<Map20Regular />} onClick={() => onTourFrom(pinned.step)}>
              {s.continueFromHere}
            </Button>
            <span className="pct-help-spacer" />
            <Button appearance="primary" size="small" onClick={onExit}>
              {s.done}
            </Button>
          </div>
        </div>
      )}

      {miss && (
        <div
          className="pct-help-miss"
          aria-live="polite"
          style={{ top: Math.min(miss.y + 14, window.innerHeight - 80), left: Math.min(miss.x + 14, window.innerWidth - 252) }}
        >
          {s.inspectMiss}
        </div>
      )}
    </>
  );
};
